import {FACTION_SOUNDS, MENU_SOUNDS} from "./menuSounds";
import {checkIfPageHidden} from "../utils/sound/checkIfSoundOutputAsleep";
import {FACTIONS} from "../constants/factions";
import {getRandomElement} from "../utils/other/getRandomElement";
import {INITIAL_VOLUME, LOCAL_STORAGE_KEY_VOLUME} from "../constants/sound";
import {releaseAudioElementVolume, setAudioElementVolume} from "../utils/sound/audioElementVolume";

class MenuSoundManager {
  constructor() {
    this.cache = new Map();
    this.factionSoundsPreloaded = false;
    // Играющий звук отсчета лобби — чтобы оборвать его (см. stopLobbyCountdown)
    this.lobbyCountdownAudio = null;
  }

  preload() {
    Object.values(MENU_SOUNDS).forEach((soundId) => {
      try {
        const audio = new Audio(`/sounds/${soundId}.mp3`);
        audio.preload = 'auto';
        this.cache.set(soundId, audio);
      } catch (e) {
        console.log(`[MenuSoundManager.preload] error: ${e}`);
      }
    });
  }

  preloadFactionSounds() {
    if (this.factionSoundsPreloaded) {
      return;
    }
    Object.values(FACTION_SOUNDS).forEach((soundId) => {
      try {
        const audio = new Audio(`/sounds/${soundId}.mp3`);
        audio.preload = 'auto';
        this.cache.set(soundId, audio);
      } catch (e) {
        console.log(`[MenuSoundManager.preloadFactionSounds] error: ${e}`);
      }
    });
    this.factionSoundsPreloaded = true;
  }

  _getGlobalVolume() {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY_VOLUME);
      if (stored === null) return INITIAL_VOLUME;
      const parsed = parseFloat(stored);
      if (isNaN(parsed)) return INITIAL_VOLUME;
      return Math.max(0, Math.min(1, parsed));
    } catch (error) {
      console.log(`[MenuSoundManager.getGlobalVolume] error: ${error}`);
      return INITIAL_VOLUME;
    }
  }

  _getAudio(soundId) {
    if (!this.cache.has(soundId)) {
      const audio = new Audio(`/sounds/${soundId}.mp3`);
      audio.preload = 'auto';
      this.cache.set(soundId, audio);
    }
    return this.cache.get(soundId);
  }

  /**
   * volumeOverride — громкость вместо общей из настроек. Нужна витрине
   * главного меню: у нее свой ползунок, и звуки, запущенные ею, обязаны идти
   * по нему, а не по громкости меню (иначе выкрученный в ноль демо-звук все
   * равно орал бы фракциями)
   *
   * Возвращает запущенный клон (null — звук не запускался): по нему звук
   * можно оборвать
   */
  _playSound(soundId, volume = 1, volumeOverride = null) {
    try {
      const globalVolume = volumeOverride === null || volumeOverride === undefined
        ? this._getGlobalVolume()
        : Math.max(0, Math.min(1, volumeOverride));
      const finalVolume = volume * globalVolume;
      if (finalVolume <= 0) {
        return null;
      }

      const audio = this._getAudio(soundId);
      const clone = audio.cloneNode();
      // Через утилиту, а не clone.volume напрямую: на iOS присваивание
      // игнорируется, и слайдер громкости ни на что не влиял
      setAudioElementVolume(clone, Math.min(finalVolume, 1));
      // Клон одноразовый, а узлы Web Audio (создаются только на iOS) держат
      // ссылку на элемент — отпускаем их, как только звук доиграл
      clone.addEventListener('ended', () => releaseAudioElementVolume(clone), {once: true});
      const playPromise = clone.play();
      if (playPromise) {
        playPromise.catch(() => {
          // Autoplay prevented or audio context locked — silently ignore
        });
      }
      return clone;
    } catch (error) {
      console.log(`[MenuSoundManager.playSound] error: ${error}`);
      return null;
    }
  }

  /**
   * Затухающие звуки фракций. В ФОНЕ не запускаются вовсе: затухание считается
   * по requestAnimationFrame, а он в скрытой вкладке не тикает — такой звук
   * навсегда застыл бы на стартовой громкости, и ни clone.pause(), ни
   * освобождение узлов Web Audio уже не случились бы.
   *
   * Остальные звуки меню (в том числе оповещение «рейтинговый матч собран»)
   * в фоне играть МОЖНО и нужно: клоны HTML-аудио играют в скрытой вкладке
   * сразу, ничего не копят и на возврате не выстреливают — в отличие от
   * звуков матча, идущих через приостановленный AudioContext Phaser
   */
  _playSoundWithEase(soundId, fadeDuration = 2000, volume = 1) {
    try {
      if (checkIfPageHidden()) {
        return;
      }
      const audio = this._getAudio(soundId);
      const clone = audio.cloneNode();
      const startVolume = volume * this._getGlobalVolume();
      setAudioElementVolume(clone, Math.min(startVolume, 1));

      const playPromise = clone.play();
      if (playPromise) {
        playPromise.catch(() => {
          // Autoplay prevented or audio context locked — silently ignore
        });
      }

      const startTime = performance.now();

      const fade = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / fadeDuration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease out
        const currentVolume = startVolume * (1 - easeProgress);

        if (progress < 1) {
          setAudioElementVolume(clone, Math.max(0, Math.min(currentVolume, 1)));
          requestAnimationFrame(fade);
        } else {
          setAudioElementVolume(clone, 0);
          clone.pause();
          clone.currentTime = 0;
          releaseAudioElementVolume(clone);
        }
      };

      requestAnimationFrame(fade);
    } catch (error) {
      console.log(`[MenuSoundManager._playSoundWithEase] error: ${error}`);
    }
  }

  // No-op fallback for game-specific sound methods that may be called
  // before Phaser scene is ready or from shared components.
  _noop() {
  }

  // =================== MENU SOUNDS ===================

  playModalRoundEnd() {
    this._playSound(MENU_SOUNDS.modal_round_end, 0.5);
  }

  playMenuButtonClick() {
    this._playSound(MENU_SOUNDS.menu_button_click, 0.4);
  }

  playCardOpen() {
    this._playSound(MENU_SOUNDS.card_open);
  }

  playChestOpen() {
    this._playSound(MENU_SOUNDS.chest_open);
  }

  playChestEternal() {
    this._playSound(MENU_SOUNDS.chest_eternal);
  }

  playCoins(coinsCount) {
    if (!coinsCount || coinsCount <= 0) {
      return;
    }
    this._playSound(getRandomElement([
      FACTION_SOUNDS.coins_many_1,
      FACTION_SOUNDS.coins_many_2,
      FACTION_SOUNDS.coins_many_3,
    ]));
  }

  playRankLobbyStart() {
    this._playSound(MENU_SOUNDS.game_start, 0.5);
  }

  playRankEnemyFound() {
    this._playSound(MENU_SOUNDS.select_unit_enemy);
  }

  playPremium() {
    this._playSound(MENU_SOUNDS.special_building_guild);
  }

  // Звон добычи: тот же звук, что у гномов при разведке рудника. Играет в
  // модалках кристаллов за рейтинг
  playMineExploration() {
    this._playSound(MENU_SOUNDS.dwarves_mine_exploration_1);
  }

  playFactionSelectIntro() {
    this._playSound(MENU_SOUNDS.faction_select, 0.3);
  }

  playFactionSelect(factionId, noIntro = false, volumeOverride = null) {
    // Все звуки этой цепочки (в том числе отложенные) идут через одну обертку,
    // чтобы громкость витрины не потерялась по дороге
    const play = (soundId, volume) => this._playSound(soundId, volume, volumeOverride);

    const factionsWithUniqueIntroSound = [
      FACTIONS.ELEMENTALS,
      FACTIONS.GIANTS,
      FACTIONS.WIZARDS,
      FACTIONS.VENTORY,
      FACTIONS.ENLIGHTENED
    ];

    let delay = 0;
    if (!noIntro && !factionsWithUniqueIntroSound.includes(factionId)) {
      play(MENU_SOUNDS.faction_select, 0.3);
      delay = 200;
    }

    setTimeout(() => {
      switch (factionId) {
        case FACTIONS.AQUARS:
          play(FACTION_SOUNDS.faction_pick_aquars_2);
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_aquars_1);
          }, 500)
          break;
        case FACTIONS.DEMONS:
          play(FACTION_SOUNDS.faction_pick_demons);
          break;
        case FACTIONS.DRAGONS:
          play(FACTION_SOUNDS.faction_pick_dragons);
          break;
        case FACTIONS.DWARVES:
          play(FACTION_SOUNDS.faction_pick_dwarves);
          break;
        case FACTIONS.ELVES:
          play(FACTION_SOUNDS.faction_pick_elves);
          break;
        case FACTIONS.GIANTS:
          play(FACTION_SOUNDS.faction_pick_giants_2, 0.5);
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_giants_3, 0.5);
          }, 1200)
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_giants_3, 0.7);
            play(FACTION_SOUNDS.faction_pick_giants);
          }, 2400)
          break;
        case FACTIONS.GOBLINS:
          play(FACTION_SOUNDS.faction_pick_goblins);
          break;
        case FACTIONS.INSECTOIDS:
          play(FACTION_SOUNDS.faction_pick_insectoids);
          break;
        case FACTIONS.SPIRITS:
          this._playSoundWithEase(FACTION_SOUNDS.faction_pick_spirits, 10000);
          break;
        case FACTIONS.UNDEAD:
          play(FACTION_SOUNDS.faction_pick_undead, 2);
          break;
        case FACTIONS.LEPRECHAUNS:
          play(FACTION_SOUNDS.leprechauns_coins);
          setTimeout(() => {
            play(FACTION_SOUNDS.leprechauns_laugh);
          }, 700)
          break;
        case FACTIONS.CELECTIALS:
          play(FACTION_SOUNDS.faction_pick_celestials, 0.8);
          break;
        case FACTIONS.BEASTMEN:
          play(FACTION_SOUNDS.faction_pick_beastmen);
          break;
        case FACTIONS.DARK_ELVES:
          play(FACTION_SOUNDS.faction_pick_dark_elves);
          break;
        case FACTIONS.SUCCUBES:
          this._playSoundWithEase(FACTION_SOUNDS.faction_pick_succubes_1, 7000, 0.5);
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_succubes, 0.6);
          }, 700)
          break;
        case FACTIONS.ENLIGHTENED:
          play(FACTION_SOUNDS.factions_pick_enlightened);
          play(FACTION_SOUNDS.coins_many_1);
          play(FACTION_SOUNDS.coins_many_2);
          play(FACTION_SOUNDS.coins_many_3);
          setTimeout(() => {
            play(FACTION_SOUNDS.coins_many_2);
            play(FACTION_SOUNDS.coins_many_3);
          }, 100)
          setTimeout(() => {
            play(FACTION_SOUNDS.coins_many_3);
            play(FACTION_SOUNDS.coins_many_1);
          }, 150)
          setTimeout(() => {
            play(FACTION_SOUNDS.coins_many_2);
            play(FACTION_SOUNDS.coins_many_1);
          }, 200)
          setTimeout(() => {
            play(FACTION_SOUNDS.coins_many_1);
            play(FACTION_SOUNDS.coins_many_2);
            play(FACTION_SOUNDS.coins_many_3);
          }, 250)
          // setTimeout(() => {
          //   this._playSoundWithEase(FACTION_SOUNDS.special_building_temple, 2100);
          // }, 1000)
          break;
        case FACTIONS.ENTS:
          play(FACTION_SOUNDS.faction_pick_ents_1, 0.5);
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_ents_2, 0.5);
          }, 500)
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_ents_3);
          }, 1000)
          break;
        case FACTIONS.FENIX:
          play(FACTION_SOUNDS.faction_pick_fenix);
          break;
        case FACTIONS.ORCS:
          play(FACTION_SOUNDS.faction_pick_orks);
          break;
        case FACTIONS.VENTORY:
          play(FACTION_SOUNDS.special_building_guild, 0.5);
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_ventory);
          }, 500)
          break;
        case FACTIONS.HUMANS:
          play(FACTION_SOUNDS.faction_pick_humans, 2);
          break;
        case FACTIONS.RATMEN:
          play(FACTION_SOUNDS.faction_pick_ratmen, 1.3);
          break;
        case FACTIONS.MUSHROOM_PEOPLE:
          play(FACTION_SOUNDS.faction_pick_mushroom_people);
          break;
        case FACTIONS.TECHNODWARVES:
          play(FACTION_SOUNDS.faction_pick_technodwarves);
          break;
        case FACTIONS.GREMLINS:
          play(FACTION_SOUNDS.faction_pick_gremlins_2, 0.3);
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_gremlins_1, 0.7);
          }, 300)
          break;
        case FACTIONS.ELEMENTALS:
          play(FACTION_SOUNDS.faction_pick_elementals_3, 0.5);
          play(FACTION_SOUNDS.faction_pick_elementals_2, 0.5);
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_elementals_1, 0.5);
          }, 300)
          break;
        case FACTIONS.WIZARDS:
          play(FACTION_SOUNDS.faction_pick_wizards_1, 0.7);
          setTimeout(() => {
            play(FACTION_SOUNDS.faction_pick_wizards_2, 2);
          }, 300)
          break;
        case FACTIONS.DOPPLERS:
          play(FACTION_SOUNDS.faction_pick_dopplers);
          break;
        default:
      }
    }, delay);
  }

  playNextCarouselItemClick() {
    this._playSound(MENU_SOUNDS.side_menu_click, 0.4);
  }

  playSideMenuClick() {
    this._playSound(MENU_SOUNDS.side_menu_click, 0.5);
  }

  // Последние секунды фазы лобби (баны, выбор фракции) — тот же звук и та же
  // громкость, что у таймера хода в матче (SoundManager.playTimer). Отдельно
  // от заглушки playTimer ниже: ее зовет таймер хода демо-партии главного
  // меню, и он заговорил бы. Звук один на экран — сам на себя не накладывается
  playLobbyCountdown() {
    if (this.lobbyCountdownAudio && !this.lobbyCountdownAudio.paused && !this.lobbyCountdownAudio.ended) {
      return;
    }
    this.lobbyCountdownAudio = this._playSound(MENU_SOUNDS.timer, 0.5);
  }

  // Игрок успел (подтвердил баны, выбрал фракцию) или фаза кончилась раньше —
  // тиканье больше ни к чему
  stopLobbyCountdown() {
    const audio = this.lobbyCountdownAudio;
    this.lobbyCountdownAudio = null;
    if (!audio) {
      return;
    }
    try {
      audio.pause();
      releaseAudioElementVolume(audio);
    } catch (error) {
      console.log(`[MenuSoundManager.stopLobbyCountdown] error: ${error}`);
    }
  }

  // =================== GAME SOUNDS (stubs) ===================
  // These are defined so components that call them before the GameScene
  // is initialised don't crash. Once GameScene takes over, the Phaser
  // SoundManager handles the real implementation.

  ensureSoundsLoaded() {
    this._noop();
  }

  playVisualToggleClick() {
    this._noop()
  }

  playVisualTogglesExpand() {
    this._noop()
  }

  playGameStart() {
    this._noop();
  }

  playPhaseSupply() {
    this._noop();
  }

  playPhaseConstruction() {
    this._noop();
  }

  playPhaseWar() {
    this._noop();
  }

  playUndoClick() {
    this._noop();
  }

  playInitiativeInterception() {
    this._noop();
  }

  playTurnEnd() {
    this._noop();
  }

  playWarStageEnd() {
    this._noop();
  }

  playStartingQuarterPlacement() {
    this._noop();
  }

  playSelectFarm() {
    this._noop();
  }

  playDistributeSupply() {
    this._noop();
  }

  playBuildingRepair() {
    this._noop();
  }

  playBuildingPlacement() {
    this._noop();
  }

  playSpecialBuildingPlacement() {
    this._noop();
  }

  playGuild() {
    this._noop();
  }

  playTemple() {
    this._noop();
  }

  playTokenClick() {
    this._noop();
  }

  playUnitRecruitmentTypeSelect() {
    this._noop();
  }

  playShooting() {
    this._noop();
  }

  playShootingD4DiceSelect() {
    this._noop();
  }

  playMeleeDiceSelect() {
    this._noop();
  }

  playRerollDices() {
    this._noop();
  }

  playRerollBadLuck() {
    this._noop();
  }

  playMeleeCombat() {
    this._noop();
  }

  playMeleeCombatAfterReroll() {
    this._noop();
  }

  playMeleeStrikes() {
    this._noop();
  }

  playPriorityTargetSelect() {
    this._noop();
  }

  playBirds() {
    this._noop();
  }

  updateBirdsVolume() {
    this._noop();
  }

  stopBirds() {
    this._noop();
  }

  playTimer() {
    this._noop();
  }

  stopTimer() {
    this._noop();
  }

  playModalCombatResults() {
    this._noop();
  }

  playUnitStarvationAgony() {
    this._noop();
  }

  playModalCityCapture() {
    this._noop();
  }

  playToastInfo() {
    this._noop();
  }

  playToastError() {
    this._noop();
  }

  playUnitSelect() {
    this._noop();
  }

  playEnemyUnit() {
    this._noop();
  }

  playUnitMovement() {
    this._noop();
  }

  playUnitUpgrade() {
    this._noop();
  }

  playUnitRecruitment() {
    this._noop();
  }

  playLeprechaunsTheft() {
    this._noop();
  }

  playDwarvesMineExploration() {
    this._noop();
  }
}

export const MENU_SOUND_MANAGER = new MenuSoundManager();
